import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ScanLine, Sparkles, Scissors, ShoppingBag, ArrowRight, CircleDollarSign } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1599834562135-b6fc90e642ca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxzdHlsaXNoJTIwbWFuJTIwaGFpcmN1dCUyMGNpbmVtYXRpYyUyMHBvcnRyYWl0fGVufDB8fHx8MTc3OTY1OTA3OHww&ixlib=rb-4.1.0&q=85";
const BARBER_IMG = "https://images.pexels.com/photos/2809652/pexels-photo-2809652.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PRODUCT_CLAY = "https://images.unsplash.com/photo-1775127741393-2b571811bb01?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxoYWlyJTIwcG9tYWRlJTIwc3R5bGluZyUyMGNsYXklMjBwcm9kdWN0fGVufDB8fHx8MTc3OTY1OTA3OHww&ixlib=rb-4.1.0&q=85";
const PRODUCT_POMADE = "https://images.unsplash.com/photo-1775126251039-214af64257ff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxoYWlyJTIwcG9tYWRlJTIwc3R5bGluZyUyMGNsYXklMjBwcm9kdWN0fGVufDB8fHx8MTc3OTY1OTA3OHww&ixlib=rb-4.1.0&q=85";

const FEATURES = [
  { label: "5 scans / 12 hrs", note: "Free, every 12 hours, on every account" },
  { label: "Refreshes automatically", note: "Rolling window — your allowance refills" },
  { label: "Every result saved", note: "Full scan history, kept forever" },
  { label: "No card on file", note: "We'll never ask. Pro+ launches soon." },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaTarget = user ? "/scan" : "/register";

  return (
    <div className="relative animate-fade-in">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[600px] w-full overflow-hidden cm-grain">
        <img
          src={HERO_IMG}
          alt="Sharp men's haircut"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/60 to-ink-950/30" />
        <div className="absolute inset-0 bg-ink-950/40" />

        <div className="relative h-full max-w-7xl mx-auto px-6 md:px-10 flex flex-col justify-end pb-20 md:pb-28">
          <p className="cm-label mb-5 animate-fade-up">AI haircut analyzer · for men</p>
          <h1
            className="cm-heading text-5xl sm:text-6xl lg:text-7xl xl:text-8xl max-w-4xl animate-fade-up"
            style={{ animationDelay: "120ms" }}
            data-testid="hero-heading"
          >
            Show us the haircut.<br />
            <span className="text-copper">We'll build your kit.</span>
          </h1>
          <p
            className="mt-6 max-w-xl text-gray-300 text-base sm:text-lg animate-fade-up"
            style={{ animationDelay: "220ms" }}
          >
            Drop a photo of any haircut. Get the exact products that build it —
            in order, in seconds. One scan. Full routine.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 animate-fade-up" style={{ animationDelay: "320ms" }}>
            <Link to={ctaTarget} className="cm-btn-primary inline-flex items-center gap-3" data-testid="hero-cta-primary">
              {user ? "Scan a haircut" : "Start scanning free"} <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="#how" className="cm-btn-secondary inline-flex items-center gap-2" data-testid="hero-cta-secondary">
              See how it works
            </a>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — Tetris/Bento */}
      <section id="how" className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <p className="cm-label">THE PROCESS</p>
        <h2 className="cm-heading text-3xl sm:text-4xl lg:text-5xl mt-3 max-w-2xl">
          Three steps from <span className="text-copper">scroll</span> to <span className="text-copper">styled</span>.
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="cm-card p-8 md:col-span-5 md:row-span-2">
            <div className="flex items-center gap-3 mb-4">
              <span className="cm-label">01</span>
              <ScanLine className="w-5 h-5 text-copper" />
            </div>
            <h3 className="cm-heading text-2xl mb-3">Upload a photo</h3>
            <p className="text-gray-400 leading-relaxed">
              Drag in a pic of the haircut you want — from Instagram, a screenshot, a buddy's photo.
              JPG, PNG, or WEBP. We'll handle the rest.
            </p>
            <img src={BARBER_IMG} alt="Barber detailing fade" className="rounded-lg mt-6 w-full h-56 object-cover" />
          </div>

          <div className="cm-card p-8 md:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="cm-label">02</span>
              <Sparkles className="w-5 h-5 text-copper" />
            </div>
            <h3 className="cm-heading text-2xl mb-3">AI breaks down the style</h3>
            <p className="text-gray-400 leading-relaxed">
              Our vision engine identifies the cut — taper level, length, texture, parting —
              then maps the exact products to build it at home.
            </p>
          </div>

          <div className="cm-card p-8 md:col-span-4">
            <div className="flex items-center gap-3 mb-4">
              <span className="cm-label">03</span>
              <Scissors className="w-5 h-5 text-copper" />
            </div>
            <h3 className="cm-heading text-2xl mb-3">Walk in confident</h3>
            <p className="text-gray-400 leading-relaxed">
              Get a copy-paste script you can read to your barber. No more "uh, kinda short on the sides".
            </p>
          </div>

          <div className="cm-card p-8 md:col-span-3 flex flex-col justify-between">
            <ShoppingBag className="w-6 h-6 text-copper" />
            <div>
              <h3 className="cm-heading text-xl mt-6 mb-2">Product matches</h3>
              <p className="text-sm text-gray-400">Clay, pomade, sea salt — picked for your texture.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-ink-900/40 border-y border-ink-700">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <p className="cm-label">PRICING</p>
          <h2 className="cm-heading text-3xl sm:text-4xl lg:text-5xl mt-3 max-w-3xl">
            Free. <span className="text-copper">Every day.</span>
          </h2>
          <p className="mt-4 text-gray-400 max-w-xl">
            5 scans every 12 hours on every account. Need more? Grab a 5-scan top-up for $2.99 — scans never expire.
          </p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((f, i) => (
              <div
                key={f.label}
                data-testid={`pricing-card-${i}`}
                className="cm-card p-8 hover:-translate-y-1 transition-all"
              >
                <p className="cm-label">FEATURE</p>
                <p className="cm-heading text-2xl mt-4">{f.label}</p>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{f.note}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-4">
            <Link to={ctaTarget} className="cm-btn-primary inline-flex items-center gap-3" data-testid="pricing-cta">
              <CircleDollarSign className="w-4 h-4" /> Start scanning free
            </Link>
          </div>
        </div>
      </section>

      {/* PRODUCT GALLERY */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
          <div>
            <p className="cm-label">THE BREAKDOWN</p>
            <h2 className="cm-heading text-3xl sm:text-4xl lg:text-5xl mt-3 max-w-md">
              Every scan = a <span className="text-copper">shopping list</span>.
            </h2>
            <p className="mt-6 text-gray-400 leading-relaxed max-w-md">
              Clay for matte hold. Pomade for shine. Sea salt for grit. The model picks the right combo
              and tells you exactly how to use them.
            </p>
            <Link to={ctaTarget} className="cm-btn-secondary mt-8 inline-flex" data-testid="gallery-cta">
              Try it free
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <img src={PRODUCT_CLAY} alt="Hair clay" className="rounded-xl h-72 sm:h-80 w-full object-cover" />
            <img src={PRODUCT_POMADE} alt="Hair pomade" className="rounded-xl h-72 sm:h-80 w-full object-cover mt-10" />
          </div>
        </div>
      </section>
    </div>
  );
}
